import React, { useState } from "react";
import { colors, fonts }    from "@/theme/tokens";
import { Button }           from "@/components/ui/Button";
import { Input }            from "@/components/ui/Input";
import { useUIStore }       from "@/store/uiStore";
import { useAuth }          from "@/hooks/useAuth";
import {
  signInWithGoogle,
  signInWithApple,
  signInWithEmail,
  signUpWithEmail,
  sendMagicLink,
  resetPassword,
} from "@/features/auth/authService";

type View = "options" | "email" | "magic" | "reset";

const OVERLAY: React.CSSProperties = {
  position: "fixed", inset: 0, zIndex: 1000,
  background: "rgba(15,10,40,.72)",
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20, animation: "adhvi-fadeIn .18s ease",
};

const CARD: React.CSSProperties = {
  background: "#fff", borderRadius: 22, overflow: "hidden",
  width: "100%", maxWidth: 390,
  border: `1px solid ${colors.border}`,
  animation: "adhvi-slideUp .22s ease",
  maxHeight: "92vh", overflowY: "auto",
};

export const SignInModal: React.FC = () => {
  const { hideModal }    = useUIStore();
  const { redirectError } = useAuth();

  const [view,     setView]     = useState<View>("options");
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading,  setLoading]  = useState<string | null>(null);
  const [error,    setError]    = useState<string | null>(redirectError);
  const [success,  setSuccess]  = useState<string | null>(null);

  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [name,     setName]     = useState("");

  const clearMessages = () => { setError(null); setSuccess(null); };

  const goTo = (v: View) => { clearMessages(); setView(v); };

  // ── Social providers (redirect flow — page navigates away, no result here) ──
  const handleProvider = (id: string, fn: () => void) => {
    setLoading(id); clearMessages();
    fn();
  };

  // ── Email / password ──
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading("email"); clearMessages();
    const res = isSignUp
      ? await signUpWithEmail(email, password, name)
      : await signInWithEmail(email, password);
    if (res.ok) { hideModal(); }
    else { setError((res as { message: string }).message); }
    setLoading(null);
  };

  // ── Magic link ──
  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading("magic"); clearMessages();
    const res = await sendMagicLink(email);
    if (res.ok) { setSuccess(res.message); }
    else { setError(res.message); }
    setLoading(null);
  };

  // ── Password reset ──
  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading("reset"); clearMessages();
    const res = await resetPassword(email);
    if (res.ok) { setSuccess(res.message); }
    else { setError(res.message); }
    setLoading(null);
  };

  const headerSubtitle = {
    options: "Sign in to borrow, share & track",
    email:   isSignUp ? "Create your free account" : "Welcome back",
    magic:   "We'll email you a one-tap link",
    reset:   "Reset your password",
  }[view];

  return (
    <div onClick={(e) => e.target === e.currentTarget && hideModal()} style={OVERLAY}>
      <div style={CARD}>

        {/* ── Header ── */}
        <div style={{
          background: colors.dark, padding: "28px 24px 22px",
          textAlign: "center", position: "relative", overflow: "hidden",
        }}>
          <div style={{ position: "absolute", width: 130, height: 130, borderRadius: "50%", background: "#3C3489", top: -42, right: -32 }} />
          <div style={{ position: "absolute", width: 75, height: 75, borderRadius: "50%", background: "#0F6E56", bottom: -24, left: 22, opacity: .5 }} />
          <button
            onClick={hideModal}
            style={{
              position: "absolute", top: 12, right: 12, zIndex: 2,
              width: 30, height: 30, background: "rgba(255,255,255,.14)",
              border: "1px solid rgba(255,255,255,.2)", borderRadius: 8,
              cursor: "pointer", fontSize: 15, color: "#fff",
            }}
          >✕</button>
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{
              width: 52, height: 52, background: "rgba(255,255,255,.14)",
              border: "1.5px solid rgba(255,255,255,.25)", borderRadius: 15,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 12px", fontSize: 24,
            }}>📚</div>
            <div style={{ fontSize: 20, fontFamily: fonts.display, fontWeight: 800, letterSpacing: "-0.5px", marginBottom: 4 }}>
              <span style={{ color: "#fff" }}>Adhvi</span><span style={{ color: "#5DCAA5" }}>Books</span>
            </div>
            <div style={{ fontSize: 11, color: "#9B97C8", fontWeight: 500 }}>{headerSubtitle}</div>
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: "20px 24px" }}>

          {/* Browse-without-sign-in note */}
          {view === "options" && (
            <div style={{
              background: colors.surfaceAlt, border: `1px solid ${colors.border}`,
              borderRadius: 10, padding: "9px 12px",
              display: "flex", alignItems: "center", gap: 8, marginBottom: 16,
            }}>
              <span style={{ fontSize: 14 }}>👁</span>
              <span style={{ fontSize: 11, color: colors.primary, lineHeight: 1.4, fontWeight: 500 }}>
                Just browsing? No sign-in needed — close this and explore freely!
              </span>
            </div>
          )}

          {/* Error / success banners */}
          {error && (
            <div style={{
              background: colors.error50, border: "1px solid #FECACA",
              borderRadius: 9, padding: "9px 12px",
              fontSize: 12, color: "#7F1D1D", marginBottom: 12, fontWeight: 500,
            }}>
              ⚠ {error}
            </div>
          )}
          {success && (
            <div style={{
              background: colors.success50, border: "1px solid #6EE7B7",
              borderRadius: 9, padding: "9px 12px",
              fontSize: 12, color: "#065F46", marginBottom: 12, fontWeight: 700,
            }}>
              ✅ {success}
            </div>
          )}

          {/* ── OPTIONS VIEW ── */}
          {view === "options" && (
            <>
              {/* Google */}
              <Button
                variant="secondary" fullWidth
                loading={loading === "google"}
                onClick={() => handleProvider("google", signInWithGoogle)}
                style={{ marginBottom: 9, justifyContent: "space-between", padding: "12px 14px" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                    <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                  </svg>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>Continue with Google</span>
                </div>
                <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 6, fontWeight: 700, background: "#D1FAE5", color: "#065F46" }}>
                  Most popular
                </span>
              </Button>

              {/* Apple */}
              <Button
                variant="secondary" fullWidth
                loading={loading === "apple"}
                onClick={() => handleProvider("apple", signInWithApple)}
                style={{ marginBottom: 9, justifyContent: "space-between", padding: "12px 14px" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
                    <path d="M13.17 9.55c-.02-2.04 1.67-3.03 1.75-3.08-0.95-1.4-2.44-1.59-2.97-1.61-1.27-.13-2.48.75-3.12.75-.64 0-1.63-.73-2.68-.71-1.38.02-2.66.8-3.37 2.03-1.44 2.5-.37 6.2 1.03 8.23.69 1 1.51 2.12 2.59 2.08 1.04-.04 1.43-.67 2.69-.67 1.25 0 1.6.67 2.7.65 1.12-.02 1.83-1.01 2.51-2.02.8-1.15 1.12-2.28 1.14-2.34-.02-.01-2.19-.84-2.21-3.31z" fill="currentColor"/>
                    <path d="M11.17 3.28c.57-.7.96-1.67.85-2.64-.82.03-1.82.55-2.41 1.24-.53.61-.99 1.59-.86 2.53.91.07 1.85-.46 2.42-1.13z" fill="currentColor"/>
                  </svg>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>Continue with Apple</span>
                </div>
                <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 6, fontWeight: 700, background: "#EDE9FE", color: "#4C1D95" }}>
                  iPhone / iPad
                </span>
              </Button>

              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0" }}>
                <div style={{ flex: 1, borderTop: `1px solid ${colors.border}` }} />
                <span style={{ fontSize: 10, color: colors.textMuted, fontWeight: 700, letterSpacing: ".06em" }}>OR</span>
                <div style={{ flex: 1, borderTop: `1px solid ${colors.border}` }} />
              </div>

              <Button variant="secondary" fullWidth onClick={() => goTo("email")} style={{ marginBottom: 9, padding: "12px 14px" }}>
                <span style={{ fontSize: 15, marginRight: 4 }}>✉</span> Continue with email
              </Button>
              <Button variant="ghost" fullWidth onClick={() => goTo("magic")} style={{ padding: "11px 14px" }}>
                <span style={{ fontSize: 14, marginRight: 4 }}>✨</span> Email me a magic link
              </Button>
            </>
          )}

          {/* ── EMAIL VIEW ── */}
          {view === "email" && (
            <form onSubmit={handleEmailSubmit}>
              {isSignUp && (
                <Input
                  label="Your name" name="name" placeholder="Adhvika"
                  value={name} onChange={e => setName(e.target.value)}
                  required inputSize="md" style={{ marginBottom: 12 }}
                />
              )}
              <Input
                label="Email address" name="email" type="email"
                placeholder="you@example.com"
                value={email} onChange={e => setEmail(e.target.value)}
                required inputSize="md" style={{ marginBottom: 12 }}
              />
              <Input
                label="Password" name="password" type="password"
                placeholder={isSignUp ? "Create a password (min 6 chars)" : "Your password"}
                value={password} onChange={e => setPassword(e.target.value)}
                required inputSize="md" style={{ marginBottom: 16 }}
              />

              <Button type="submit" variant="primary" fullWidth loading={loading === "email"}>
                {isSignUp ? "🚀 Create my account" : "🔐 Sign in"}
              </Button>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 12 }}>
                <span style={{ fontSize: 11, color: colors.textSub }}>
                  {isSignUp
                    ? <span>Have an account? <button type="button" onClick={() => setIsSignUp(false)} style={{ color: colors.primary, fontWeight: 700, background: "none", border: "none", cursor: "pointer" }}>Sign in</button></span>
                    : <span>No account? <button type="button" onClick={() => setIsSignUp(true)} style={{ color: colors.primary, fontWeight: 700, background: "none", border: "none", cursor: "pointer" }}>Sign up free</button></span>
                  }
                </span>
                {!isSignUp && (
                  <button
                    type="button"
                    onClick={() => goTo("reset")}
                    style={{ fontSize: 11, color: colors.textSub, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
                  >
                    Forgot password?
                  </button>
                )}
              </div>
            </form>
          )}

          {/* ── MAGIC LINK VIEW ── */}
          {view === "magic" && !success && (
            <form onSubmit={handleMagicLink}>
              <p style={{ fontSize: 12, color: colors.textSub, marginBottom: 14, lineHeight: 1.6 }}>
                Enter your email and we'll send a one-click sign-in link — no password needed.
              </p>
              <Input
                label="Email address" name="magic-email" type="email"
                placeholder="you@example.com"
                value={email} onChange={e => setEmail(e.target.value)}
                required inputSize="md" style={{ marginBottom: 16 }}
              />
              <Button type="submit" variant="primary" fullWidth loading={loading === "magic"}>
                ✨ Send magic link
              </Button>
            </form>
          )}

          {/* ── RESET PASSWORD VIEW ── */}
          {view === "reset" && !success && (
            <form onSubmit={handleReset}>
              <p style={{ fontSize: 12, color: colors.textSub, marginBottom: 14, lineHeight: 1.6 }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>
              <Input
                label="Email address" name="reset-email" type="email"
                placeholder="you@example.com"
                value={email} onChange={e => setEmail(e.target.value)}
                required inputSize="md" style={{ marginBottom: 16 }}
              />
              <Button type="submit" variant="primary" fullWidth loading={loading === "reset"}>
                📧 Send reset link
              </Button>
            </form>
          )}

          {/* ── SUCCESS STATE (magic / reset) ── */}
          {success && (view === "magic" || view === "reset") && (
            <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
              <div style={{ fontSize: 44, marginBottom: 10 }}>{view === "magic" ? "✨" : "📧"}</div>
              <button
                type="button"
                onClick={() => goTo("options")}
                style={{ marginTop: 12, fontSize: 12, color: colors.primary, background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}
              >
                ← Back to sign-in options
              </button>
            </div>
          )}

          {/* Back link */}
          {view !== "options" && !success && (
            <button
              type="button"
              onClick={() => goTo("options")}
              style={{ display: "block", margin: "14px auto 0", fontSize: 12, color: colors.textSub, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
            >
              ← Other sign-in options
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
